/**
 * The simulated context a run executes in (docs/sandbox.md §5, ADR-0017):
 * with the code and the seed, it fixes what a run does. Defaults live with
 * each environment profile (e.g. core/environments/browser).
 */
import type { SeedCookie } from "../cookies";
import { isObject } from "../events";
import type { MockRoute } from "../network";

/** A coherent browser/device identity for `navigator`, `screen`, and `window` sizes. */
export interface DeviceProfile {
  userAgent: string;
  /** `navigator.platform`, e.g. `MacIntel` or `Win32`. */
  platform: string;
  /** `navigator.userAgentData.platform`, e.g. `macOS`. */
  uaPlatform: string;
  mobile: boolean;
  /** Major Chrome version (for `userAgentData.brands`). */
  chromeVersion: number;
  hardwareConcurrency: number;
  deviceMemory: number;
  maxTouchPoints: number;
  screen: {
    width: number;
    height: number;
    availWidth: number;
    availHeight: number;
    colorDepth: number;
  };
  devicePixelRatio: number;
  viewport: { innerWidth: number; innerHeight: number; outerWidth: number; outerHeight: number };
}

/**
 * Simulated user activity (docs/sandbox.md §3.2, ADR-0017): what the user
 * does once the page has loaded, on the virtual clock.
 */
export interface SimActivity {
  /**
   * Type the run's form-field canaries into the page's sensitive fields,
   * one key at a time (focus, keydown, keypress, input, keyup, change, blur).
   */
  fill: boolean;
  /** Then click each typed-in form's submit button and fire `submit` (nothing navigates). */
  submit: boolean;
  /** Virtual ms after the window's `load` before the user starts (default 1000). */
  delayMs?: number;
}

export interface SimContext {
  /** The fake current date, in epoch ms. The virtual clock starts here. */
  now: number;
  /** The page's URL; relative URLs resolve against it. */
  url: string;
  /** `document.referrer`. */
  referrer: string;
  /** The page's initial HTML, which seeds the DOM. */
  html: string;
  /** BCP 47 language tags, preferred first (`navigator.languages`). */
  languages: string[];
  /** The IANA time zone `Intl` reports (Date arithmetic stays in UTC; see sandbox.md §7). */
  timezone: string;
  device: DeviceProfile;
  /** Third-party globals to fake (docs/sandbox.md §5), e.g. `ga`, `jQuery`, `ethereum`. */
  decoys: string[];
  /** Cookies already set for the page (HttpOnly ones are invisible to scripts). */
  cookies: SeedCookie[];
  localStorage: Record<string, string>;
  sessionStorage: Record<string, string>;
  /** Simulated responses (first match wins). */
  mockRoutes: MockRoute[];
  /** What the simulated user does after load; nothing when absent. */
  activity?: SimActivity;
}

/** The largest epoch ms a `Date` can hold. */
const MAX_DATE = 8.64e15;

const isString = (v: unknown): v is string => typeof v === "string";

export function parseStringRecord(v: unknown): Record<string, string> | null {
  if (!isObject(v) || !Object.values(v).every(isString)) return null;
  return { ...(v as Record<string, string>) };
}

export function parseSeedCookie(v: unknown): SeedCookie | null {
  if (!isObject(v) || !isString(v.name) || !isString(v.value)) return null;
  const cookie: SeedCookie = { name: v.name, value: v.value };
  if (v.domain !== undefined) {
    if (!isString(v.domain)) return null;
    cookie.domain = v.domain;
  }
  if (v.path !== undefined) {
    if (!isString(v.path)) return null;
    cookie.path = v.path;
  }
  for (const flag of ["httpOnly", "secure"] as const) {
    if (v[flag] === undefined) continue;
    if (typeof v[flag] !== "boolean") return null;
    cookie[flag] = v[flag];
  }
  return cookie;
}

export function parseMockRoute(v: unknown): MockRoute | null {
  if (!isObject(v) || !isString(v.url)) return null;
  const route: MockRoute = { url: v.url };
  if (v.method !== undefined) {
    if (!isString(v.method)) return null;
    route.method = v.method;
  }
  for (const key of ["status", "delayMs"] as const) {
    if (v[key] === undefined) continue;
    const n = v[key];
    if (!Number.isInteger(n) || (n as number) < 0) return null;
    route[key] = n as number;
  }
  if (v.headers !== undefined) {
    const headers = parseStringRecord(v.headers);
    if (!headers) return null;
    route.headers = headers;
  }
  if (v.body !== undefined) {
    if (!isString(v.body)) return null;
    route.body = v.body;
  }
  if (v.error !== undefined) {
    if (typeof v.error !== "boolean") return null;
    route.error = v.error;
  }
  if (v.messages !== undefined) {
    if (!Array.isArray(v.messages) || !v.messages.every(isString)) return null;
    route.messages = [...v.messages];
  }
  return route;
}

const isNumber = (v: unknown): v is number => typeof v === "number" && Number.isFinite(v) && v >= 0;
const isStringList = (v: unknown): v is string[] => Array.isArray(v) && v.every(isString);

function parseNumbers<K extends string>(v: unknown, keys: readonly K[]): Record<K, number> | null {
  if (!isObject(v)) return null;
  const out = {} as Record<K, number>;
  for (const key of keys) {
    if (!isNumber(v[key])) return null;
    out[key] = v[key] as number;
  }
  return out;
}

export function parseDevice(v: unknown): DeviceProfile | null {
  if (!isObject(v)) return null;
  const numbers = parseNumbers(v, [
    "chromeVersion",
    "hardwareConcurrency",
    "deviceMemory",
    "maxTouchPoints",
    "devicePixelRatio",
  ] as const);
  const screen = parseNumbers(v.screen, [
    "width",
    "height",
    "availWidth",
    "availHeight",
    "colorDepth",
  ] as const);
  const viewport = parseNumbers(v.viewport, [
    "innerWidth",
    "innerHeight",
    "outerWidth",
    "outerHeight",
  ] as const);
  if (!numbers || !screen || !viewport) return null;
  if (!isString(v.userAgent) || !isString(v.platform) || !isString(v.uaPlatform)) return null;
  if (typeof v.mobile !== "boolean") return null;
  return {
    userAgent: v.userAgent,
    platform: v.platform,
    uaPlatform: v.uaPlatform,
    mobile: v.mobile,
    ...numbers,
    screen,
    viewport,
  };
}

export function parseActivity(v: unknown): SimActivity | null {
  if (!isObject(v) || typeof v.fill !== "boolean" || typeof v.submit !== "boolean") return null;
  const activity: SimActivity = { fill: v.fill, submit: v.submit };
  if (v.delayMs !== undefined) {
    if (!Number.isSafeInteger(v.delayMs) || (v.delayMs as number) < 0) return null;
    activity.delayMs = v.delayMs as number;
  }
  return activity;
}

/** Validates a context received over the protocol; returns a copy, or null. */
export function parseSimContext(v: unknown): SimContext | null {
  if (!isObject(v)) return null;
  const { now, url, html, referrer, timezone } = v;
  if (!Number.isSafeInteger(now) || (now as number) < 0 || (now as number) > MAX_DATE) return null;
  if (!isString(url) || !isString(html) || !Array.isArray(v.cookies)) return null;
  if (!isString(referrer) || !isString(timezone)) return null;
  if (!isStringList(v.languages) || v.languages.length === 0 || !isStringList(v.decoys))
    return null;
  const device = parseDevice(v.device);
  if (!device) return null;
  const cookies = v.cookies.map(parseSeedCookie);
  const localStorage = parseStringRecord(v.localStorage);
  const sessionStorage = parseStringRecord(v.sessionStorage);
  if (!cookies.every((c) => c !== null) || !localStorage || !sessionStorage) return null;
  if (!Array.isArray(v.mockRoutes)) return null;
  const mockRoutes = v.mockRoutes.map(parseMockRoute);
  if (!mockRoutes.every((r) => r !== null)) return null;
  const activity = v.activity === undefined ? undefined : parseActivity(v.activity);
  if (activity === null) return null;
  return {
    now: now as number,
    url,
    referrer,
    html,
    languages: [...v.languages],
    timezone,
    device,
    decoys: [...v.decoys],
    cookies,
    localStorage,
    sessionStorage,
    mockRoutes,
    ...(activity && { activity }),
  };
}

export interface Limits {
  memoryBytes: number;
  stackBytes: number;
  /** Total guest execution time per run. */
  cpuMs: number;
  /** Guest events recorded per run. */
  events: number;
  /** How far the virtual clock may run ahead of the start. */
  virtualMs: number;
  /** Timer callbacks fired per run. */
  timerFires: number;
}

/** docs/sandbox.md §2.1. */
export const DEFAULT_LIMITS: Limits = {
  memoryBytes: 64 * 1024 * 1024,
  stackBytes: 1024 * 1024,
  cpuMs: 5000,
  events: 50_000,
  virtualMs: 365 * 24 * 60 * 60 * 1000,
  timerFires: 10_000,
};

/** How long past the CPU limit the UI waits before killing a silent worker. */
export const WATCHDOG_GRACE_MS = 5000;

/** Validates a list with `parse`; null if it isn't an array or any item is invalid. */
export function parseList<T>(v: unknown, parse: (item: unknown) => T | null): T[] | null {
  if (!Array.isArray(v)) return null;
  const out = v.map(parse);
  return out.every((item) => item !== null) ? (out as T[]) : null;
}
